import React, { useState, useEffect } from "react";
import { CreditCard, Check, RefreshCw, Loader2, ChevronDown, User, Home, FileDown, Mail } from "lucide-react";
import { getAdminCharges, confirmCharge, generateRent, downloadOwnerStatement, emailOwnerStatements } from "../../api";
import { useToast } from "../../hooks/use-toast";

const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "\u2014");
const cColor = { paid: "bg-green-100 text-green-700", due: "bg-amber-100 text-amber-700", "awaiting-confirmation": "bg-blue-100 text-blue-700" };

const AdminPayments = () => {
  const { toast } = useToast();
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openOwner, setOpenOwner] = useState(null);
  const refresh = () => getAdminCharges().then(setCharges).catch(() => setCharges([]));
  useEffect(() => { refresh(); }, []);

  const confirm = async (id) => {
    try { await confirmCharge(id); toast({ title: "Payment confirmed" }); refresh(); }
    catch { toast({ title: "Couldn't confirm" }); }
  };
  const genRent = async () => {
    setBusy(true);
    try { const r = await generateRent(); toast({ title: "Rent generated", description: `${r.created} new charge(s) created.` }); refresh(); }
    catch { toast({ title: "Generation failed" }); } finally { setBusy(false); }
  };
  const emailStatements = async () => {
    setBusy(true);
    try {
      const r = await emailOwnerStatements();
      toast({ title: r.sent ? `Statements emailed to ${r.sent} landlord(s)` : "Email not active yet", description: r.message || `Owner statements for ${r.period} are on their way.` });
    } catch { toast({ title: "Couldn't send statements" }); } finally { setBusy(false); }
  };

  // owner -> property -> charges
  const owners = {};
  charges.forEach((c) => {
    const o = (owners[c.owner] ||= { owner: c.owner, total: 0, outstanding: 0, props: {} });
    const p = (o.props[c.property] ||= { property: c.property, image: c.propertyImage, charges: [] });
    p.charges.push(c);
    o.total += c.amount || 0;
    if (c.status !== "paid") o.outstanding += c.amount || 0;
  });
  const ownerList = Object.values(owners).sort((a, b) => b.total - a.total);
  const awaiting = charges.filter((c) => c.status === "awaiting-confirmation");

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-payments">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><CreditCard size={19} className="text-[#b8935f]" /> Payments — by Owner & Property</h3>
        <div className="flex items-center gap-2">
          <button onClick={emailStatements} disabled={busy} data-testid="email-statements-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#e2d8c8] text-[#6a6a6a] text-[13px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60"><Mail size={14} /> Email statements</button>
          <button onClick={genRent} disabled={busy} data-testid="generate-rent-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Generate monthly rent</button>
        </div>
      </div>

      {awaiting.length > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-100" data-testid="awaiting-block">
          <p className="text-[13px] font-500 text-blue-800 mb-2">Awaiting confirmation ({awaiting.length})</p>
          <div className="space-y-2">
            {awaiting.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100" data-testid="awaiting-charge-row">
                <div><p className="text-[14px] text-[#2b2b2b]">{c.resident} — {c.label}</p><p className="text-[12px] text-[#a0a0a0]">{fmt(c.amount)} · {c.property} · marked paid {fmtDate(c.paidAt)}</p></div>
                <button onClick={() => confirm(c.id)} data-testid="confirm-charge-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-[12px] font-500 hover:bg-green-700 transition-colors"><Check size={14} /> Confirm receipt</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {ownerList.length === 0 ? <p className="p-8 text-center text-[#8a8a8a]">No charges yet.</p> : ownerList.map((o) => (
          <div key={o.owner} className="rounded-xl border border-[#f0ece5] overflow-hidden" data-testid="owner-group">
            <button onClick={() => setOpenOwner(openOwner === o.owner ? null : o.owner)} className="w-full flex items-center justify-between px-4 py-3.5 bg-[#faf7f2] hover:bg-[#f4efe6] transition-colors" data-testid="owner-toggle">
              <span className="flex items-center gap-2.5 text-[15px] font-500 text-[#2b2b2b]"><span className="w-8 h-8 rounded-full bg-[#b8935f]/10 flex items-center justify-center"><User size={15} className="text-[#b8935f]" /></span>{o.owner}<span className="text-[12px] text-[#a0a0a0] font-400">· {Object.keys(o.props).length} propert{Object.keys(o.props).length === 1 ? "y" : "ies"}</span></span>
              <span className="flex items-center gap-3">
                {o.outstanding > 0 && <span className="text-[12px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">{fmt(o.outstanding)} outstanding</span>}
                <span className="text-[13px] text-[#6a6a6a]">{fmt(o.total)} total</span>
                {o.owner !== "Unassigned" && <span onClick={(e) => { e.stopPropagation(); downloadOwnerStatement(o.owner); }} data-testid="owner-statement-btn" title="Download owner statement PDF" className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded-lg border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors cursor-pointer"><FileDown size={13} /> Statement</span>}
                <ChevronDown size={17} className={`text-[#a0a0a0] transition-transform ${openOwner === o.owner ? "rotate-180" : ""}`} />
              </span>
            </button>
            {openOwner === o.owner && (
              <div className="p-4 space-y-4">
                {Object.values(o.props).map((p) => (
                  <div key={p.property} data-testid="property-group">
                    <p className="text-[13px] font-500 text-[#2b2b2b] mb-2 flex items-center gap-2"><Home size={14} className="text-[#b8935f]" /> {p.property}</p>
                    <div className="space-y-1.5 pl-1">
                      {p.charges.map((c) => (
                        <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-[#f0ece5]" data-testid="charge-detail-row">
                          <div><p className="text-[13px] text-[#2b2b2b]">{c.label} <span className="text-[11px] text-[#a0a0a0]">· {c.resident}</span></p><p className="text-[11px] text-[#a0a0a0]">Due {fmtDate(c.dueDate)}</p></div>
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-500 text-[#2b2b2b]">{fmt(c.amount)}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${cColor[c.status] || "bg-gray-100"}`}>{c.status.replace("-", " ")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPayments;
